/**
 * aC Men's Hub の「案件」で札を動かす（全社スプシの行の位置を書き換える）ための語彙と API 契約。
 *
 * 動かせる先の決まり（区分の中の順番・区分の付け替え・未振り分けからの置き場所決め）、反映しない時間帯の
 * 判定、下書き・反映の結果・動かした記録の形を、API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）で
 * 同じ関数と型から扱う。画面が「落とせる区分」「HH:50 から」「未反映の n件」を出す判定と、API が反映を
 * 受け付ける判定を1か所にまとめ、2つが食い違わないようにする。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business メンズの案件の並べ替え（動かせる先・反映しない時間帯・下書き・反映の結果・動かした記録）の決まりと API の契約を API と Web で共有する
 * @graph-connects none
 */
import { z } from 'zod';
import { ProductBacklogSectionKindSchema, } from './product-backlog.js';
import { ProductBacklogWriteBlockReasonSchema } from './product-backlog-window.js';
/**
 * 区分ごとに、札を落とせる区分（自分自身を含めば区分の中の順番を動かせる）。
 * - 開発アサイン希望（要件定義済み）は区分の中の順番だけ
 * - 要件定義アサイン希望・優先度未決定・保留は区分の中と、この3つの間
 * - 未振り分け（置き場所が決まっていない案件）からはこの3つへ置くだけ（未振り分けへは戻さない）
 * - 見るだけの区分はどこへも動かせない
 * @graph-connects none
 */
export const PRODUCT_BACKLOG_MOVE_TARGETS = {
    unassigned: ['req_waiting', 'undecided', 'hold'],
    dev_waiting: ['dev_waiting'],
    req_waiting: ['req_waiting', 'undecided', 'hold'],
    undecided: ['req_waiting', 'undecided', 'hold'],
    hold: ['req_waiting', 'undecided', 'hold'],
    req_assigned: [],
    dev_assigned: [],
    light_task: [],
    done: [],
};
/**
 * from の区分の札を to の区分へ落とせるか。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の札をある区分から別の区分（または同じ区分の別の位置）へ落とせるかを、区分の組み合わせで決める
 * @graph-connects none
 */
export function canMoveCard(from, to) {
    return PRODUCT_BACKLOG_MOVE_TARGETS[from].includes(to);
}
/**
 * 札の「…」の移動メニューに出す区分（今いる区分は出さない。置き先の末尾へ入る）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の札の移動メニューに出す行き先の区分（今いる区分を除いた、付け替えできる区分）を返す
 * @graph-connects none
 */
export function menuTargetsOf(from) {
    return PRODUCT_BACKLOG_MOVE_TARGETS[from].filter((kind) => kind !== from);
}
/**
 * 区分1つの並びの中身: キー（キーの無い行は null）を行の順に並べたもの。
 * @graph-connects none
 */
const SectionTokensSchema = z.array(z.string().min(1).max(64).nullable()).max(1000);
/**
 * 下書きの区分1つ。base は下書きを作ったときの並び、target は動かした後の並び。
 * 未振り分けの base は一覧に出ていた札だけ（メンズの行）、target は base から置いた札を除いたもの。
 * @graph-connects none
 */
export const ProductBacklogDraftSectionSchema = z.object({
    kind: ProductBacklogSectionKindSchema,
    label: z.string().min(1).max(200),
    base: SectionTokensSchema,
    target: SectionTokensSchema,
});
/**
 * 並べ替えの下書き（動かす人ごとに1つ）。sections は並びが変わった区分だけ。
 * baseReadAt は下書きの元にしたスプシを読んだ時刻。
 * @graph-connects none
 */
export const ProductBacklogDraftSchema = z.object({
    createdAt: z.string(),
    updatedAt: z.string(),
    baseReadAt: z.string(),
    sections: z.array(ProductBacklogDraftSectionSchema).max(5),
});
/**
 * 下書きを保存する入力。sections が空なら下書きを消す。
 * @graph-connects none
 */
export const ProductBacklogSaveDraftInputSchema = z.object({
    baseReadAt: z.string().min(1).max(64),
    sections: z.array(ProductBacklogDraftSectionSchema).max(5),
});
/**
 * 下書きの読み取り・保存の応答（下書きが無ければ null）。
 * @graph-connects none
 */
export const ProductBacklogDraftResponseSchema = z.object({
    draft: ProductBacklogDraftSchema.nullable(),
});
/**
 * 反映の入力。draftUpdatedAt は画面が見ている下書きの更新時刻（別の画面で直した下書きは反映しない）。
 * @graph-connects none
 */
export const ProductBacklogApplyInputSchema = z.object({
    draftUpdatedAt: z.string().min(1).max(64),
});
/**
 * 元に戻す入力。batchId は直前の反映の記録。
 * @graph-connects none
 */
export const ProductBacklogUndoInputSchema = z.object({
    batchId: z.string().min(1).max(64),
});
/**
 * 反映・元に戻すの結果。
 * - applied / undone: 書いて、読み戻した並びが期待どおり（count は動かした札の数）
 * - drifted: 書いたが、読み戻した並びが期待と違う（自動では直さない）
 * - unverified: 書いたが、読み戻せず確かめられなかった
 * - stale: 下書き（元に戻すなら直前の反映）の後に、影響する区分の並びがスプシで変わっていた（何も書かない）
 * - drift_blocked: 影響する区分に書き写しのずれがある（何も書かない）
 * - blocked_window: 反映しない時間帯（resumeAt から反映できる）
 * - switch_off: 書き込みのスイッチがオフか、設定を読めない
 * - busy: ほかの画面で反映中（鍵が取れない）
 * - layout_changed: スプシの区切りや見出しの形が変わっていて読むだけにしている
 * - forbidden: スプシの編集権限が無い / unavailable: スプシが混み合っている・障害
 * - no_draft: 下書きが無い / draft_outdated: 画面の下書きより新しい下書きがある
 * - undo_unavailable: 元に戻せる反映が無い（戻すのは直前の1段だけ）
 * @graph-connects none
 */
export const ProductBacklogApplyResultSchema = z.discriminatedUnion('outcome', [
    z.object({ outcome: z.literal('applied'), batchId: z.string(), count: z.number().int() }),
    z.object({ outcome: z.literal('undone'), batchId: z.string(), count: z.number().int() }),
    z.object({ outcome: z.literal('drifted'), batchId: z.string(), count: z.number().int() }),
    z.object({ outcome: z.literal('unverified'), batchId: z.string(), count: z.number().int() }),
    z.object({
        outcome: z.literal('stale'),
        changed: z.array(z.object({ key: z.string().nullable(), title: z.string().nullable() })),
    }),
    z.object({
        outcome: z.literal('drift_blocked'),
        sectionKinds: z.array(ProductBacklogSectionKindSchema),
    }),
    z.object({
        outcome: z.literal('blocked_window'),
        resumeAt: z.string(),
        reason: ProductBacklogWriteBlockReasonSchema,
    }),
    z.object({ outcome: z.literal('switch_off') }),
    z.object({ outcome: z.literal('busy') }),
    z.object({ outcome: z.literal('layout_changed') }),
    z.object({ outcome: z.literal('forbidden') }),
    z.object({ outcome: z.literal('unavailable') }),
    z.object({ outcome: z.literal('no_draft') }),
    z.object({ outcome: z.literal('draft_outdated') }),
    z.object({ outcome: z.literal('undo_unavailable') }),
]);
/**
 * 動かした記録の札1枚の位置（rank は区分の中で何番目か。未振り分けなど数えないときは null）。
 * @graph-connects none
 */
const MovePositionSchema = z.object({
    kind: ProductBacklogSectionKindSchema,
    rank: z.number().int().positive().nullable(),
});
/**
 * 動かした記録の札1枚。
 * @graph-connects none
 */
export const ProductBacklogMoveRowSchema = z.object({
    key: z.string(),
    title: z.string().nullable(),
    from: MovePositionSchema,
    to: MovePositionSchema,
});
/**
 * 記録の結果: 反映した / 見送った / ずれた / 確かめられなかった / 戻した / ずれを見つけた。
 * @graph-connects none
 */
export const ProductBacklogMoveOutcomeSchema = z.enum([
    'applied',
    'skipped',
    'drifted',
    'unverified',
    'undone',
    'drift_found',
]);
/**
 * 見送った・ずれたときの理由。
 * @graph-connects none
 */
export const ProductBacklogMoveReasonSchema = z.enum([
    'sheet_changed',
    'drift_in_section',
    'sheet_forbidden',
    'sheet_unavailable',
    'readback_mismatch',
    'readback_unavailable',
    'sync_window',
    'lock_expiring',
]);
/**
 * 動かした記録1件（1回の反映・元に戻す・ずれの発見）。by は動かした人（ずれの発見は null）。
 * drift はずれを見つけた記録だけが持つ（行・E列のキー・F列のリンク先のキー）。
 * @graph-connects none
 */
export const ProductBacklogMoveRecordSchema = z.object({
    batchId: z.string(),
    at: z.string(),
    by: z.string().nullable(),
    action: z.enum(['apply', 'undo', 'detect']),
    outcome: ProductBacklogMoveOutcomeSchema,
    reason: ProductBacklogMoveReasonSchema.nullable(),
    rows: z.array(ProductBacklogMoveRowSchema),
    drift: z
        .object({
        rowNumber: z.number().int().positive(),
        sheetKey: z.string(),
        linkKey: z.string(),
    })
        .nullable(),
});
/**
 * 動かした記録の一覧（新しい順）。undoableBatchId は元に戻せる直前の反映（無ければ null）。
 * @graph-connects none
 */
export const ProductBacklogMovesResponseSchema = z.object({
    records: z.array(ProductBacklogMoveRecordSchema),
    undoableBatchId: z.string().nullable(),
});
/**
 * 2つの並び（同じ id を持ちうる2つの配列）の、順番を保ったまま両方に残せる id の集まりを返す（重み付きの
 * 最長共通部分列）。pinned の id は動かせない行なので、重みを大きくして必ず残す側に入れる（入りきらない並びは
 * 呼び出し側で弾く）。残らない id が「動かした」札になる。id はどちらの配列の中でも重ならないこと。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 並べ替えの前後の並びから、順番を変えずに済む札（動かさない札）を最も多く選び、残りを動かした札として決める
 * @graph-connects none
 */
export function keptInOrder(before, after, pinned) {
    const weightOf = (id) => (pinned.has(id) ? before.length + after.length + 1 : 1);
    const width = after.length + 1;
    const score = new Array((before.length + 1) * width).fill(0);
    const cell = (i, j) => score[i * width + j] ?? 0;
    before.forEach((a, bi) => {
        after.forEach((b, aj) => {
            const best = Math.max(cell(bi, aj + 1), cell(bi + 1, aj));
            score[(bi + 1) * width + aj + 1] =
                a === b ? Math.max(best, cell(bi, aj) + weightOf(a)) : best;
        });
    });
    const kept = new Set();
    let i = before.length;
    let j = after.length;
    while (i > 0 && j > 0) {
        const a = before[i - 1];
        if (a === after[j - 1] && a !== undefined && cell(i, j) === cell(i - 1, j - 1) + weightOf(a)) {
            kept.add(a);
            i -= 1;
            j -= 1;
        }
        else if (cell(i, j) === cell(i - 1, j)) {
            i -= 1;
        }
        else {
            j -= 1;
        }
    }
    return kept;
}
/**
 * 区分ごとの並べ替えの前後（before / after）から、動かした札を決める。別の区分へ移った札は全部、
 * 同じ区分に残った札は順番を保ったまま残せない分だけを、動かした札にする。after の区分の順で、上から返す。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 区分ごとの並べ替えの前後から、別の区分へ移した札と、区分の中で順番を変えた札（最小限）を、動かした札として決める
 * @graph-connects keptInOrder [calls] 順番を保ったまま残せる札
 */
export function movedCards(before, after, pinned) {
    const fromOf = new Map();
    for (const [kind, ids] of before)
        for (const id of ids)
            fromOf.set(id, kind);
    const moves = [];
    for (const [kind, ids] of after) {
        const stayed = ids.filter((id) => fromOf.get(id) === kind);
        const kept = keptInOrder((before.get(kind) ?? []).filter((id) => stayed.includes(id)), stayed, pinned);
        for (const id of ids) {
            const from = fromOf.get(id);
            if (from === undefined)
                continue;
            if (from !== kind || !kept.has(id))
                moves.push({ id, from, to: kind });
        }
    }
    return moves;
}
//# sourceMappingURL=product-backlog-write.js.map